import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { IconPicker, SCORE_COLORS } from '@traquility/ui/diary';
import type { Icon, IconPickerLabels } from '@traquility/ui/diary';

// Create a query client for this component tree
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes - icons rarely change
    },
  },
});

// GraphQL query for fetching icons
const ICONS_QUERY = `
  query Icons($name: String, $category: String, $style: IconStyle) {
    icons(name: $name, category: $category, style: $style) {
      key
      name
      category
      path
      style
    }
    iconCategories {
      key
      label
    }
  }
`;

export interface IconPickerContainerProps {
  /** GraphQL endpoint URL */
  graphqlEndpoint: string;
  /** CSRF token for API requests */
  csrfToken: string;
  /** Initially selected icon key */
  initialIconKey?: string;
  /** Initially selected score */
  initialScore?: number;
  /** Callback when an icon is selected */
  onSelect: (iconKey: string, score: number) => void;
  /** Callback when score/color changes */
  onScoreChange?: (score: number) => void;
  /** Labels for i18n */
  labels?: IconPickerLabels;
}

interface IconCategory {
  key: string;
  label: string;
}

interface IconsQueryResult {
  data?: {
    icons: Icon[];
    iconCategories: IconCategory[];
  };
  errors?: Array<{ message: string }>;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

function IconPickerInner({
  graphqlEndpoint,
  csrfToken,
  initialIconKey,
  initialScore = 100,
  onSelect,
  onScoreChange: onScoreChangeCallback,
  labels,
}: IconPickerContainerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [style, setStyle] = useState<'default' | 'stroked'>('default');
  const [selectedIcon, setSelectedIcon] = useState<string | null>(initialIconKey ?? null);
  const [selectedScore, setSelectedScore] = useState(initialScore);
  // Cache the selected icon data so we can always show it
  const [selectedIconData, setSelectedIconData] = useState<Icon | null>(null);

  // Debounce search term to avoid too many API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Fetch icons via GraphQL
  const { data, isLoading } = useQuery<IconsQueryResult>({
    queryKey: ['icons', debouncedSearchTerm, category, style],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: ICONS_QUERY,
          variables: {
            name: debouncedSearchTerm || null,
            category: category,
            style: style.toUpperCase(),
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch icons');
      }

      return response.json();
    },
  });

  // Cache selected icon data when it appears in results
  useEffect(() => {
    if (selectedIcon && data?.data?.icons) {
      const iconData = data.data.icons.find(icon => icon.key === selectedIcon);
      if (iconData) {
        setSelectedIconData(iconData);
      }
    }
  }, [selectedIcon, data?.data?.icons]);

  const handleSelect = useCallback(
    (iconKey: string) => {
      setSelectedIcon(iconKey);
      // Cache the selected icon data immediately
      const iconData = data?.data?.icons.find(icon => icon.key === iconKey);
      if (iconData) {
        setSelectedIconData(iconData);
      }
      // Call onSelect with current score - this closes the modal
      onSelect(iconKey, selectedScore);
    },
    [onSelect, selectedScore, data?.data?.icons]
  );

  const handleScoreChange = useCallback(
    (score: number) => {
      // Update the score state, don't close the modal
      setSelectedScore(score);
      // Notify parent about score change
      onScoreChangeCallback?.(score);
    },
    [onScoreChangeCallback]
  );

  // Custom icon renderer that uses CSS classes - compact 40x40 size
  const renderIcon = useCallback(
    (icon: Icon, score: number) => (
      <div
        className="icon-wrapper"
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: SCORE_COLORS[score] ?? SCORE_COLORS[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <i className={`icon-flex icon-${icon.key}`} style={{ transform: 'scale(0.7)' }} />
      </div>
    ),
    []
  );

  // Merge selected icon into the list if it's not already there
  const iconsToDisplay = React.useMemo(() => {
    const fetchedIcons = data?.data?.icons ?? [];

    // If no selected icon or it's already in the list, return as is
    if (!selectedIcon || !selectedIconData) {
      return fetchedIcons;
    }

    const isSelectedInList = fetchedIcons.some(icon => icon.key === selectedIcon);
    if (isSelectedInList) {
      return fetchedIcons;
    }

    // Add selected icon at the beginning so it's easy to find
    return [selectedIconData, ...fetchedIcons];
  }, [data?.data?.icons, selectedIcon, selectedIconData]);

  return (
    <IconPicker
      selectedIcon={selectedIcon}
      selectedScore={selectedScore}
      onSelect={handleSelect}
      onScoreChange={handleScoreChange}
      icons={iconsToDisplay}
      categories={data?.data?.iconCategories ?? []}
      isLoading={isLoading}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      category={category}
      onCategoryChange={setCategory}
      style={style}
      onStyleChange={setStyle}
      labels={labels}
      renderIcon={renderIcon}
    />
  );
}

/**
 * IconPickerContainer - Entry point for rendering icon picker in Rails
 *
 * This component wraps the shared IconPicker with:
 * - React Query provider
 * - GraphQL query handling with debounced search
 * - CSRF token handling for Rails
 */
export function IconPickerContainer(props: IconPickerContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <IconPickerInner {...props} />
    </QueryClientProvider>
  );
}

// Store root reference for cleanup
let iconPickerRoot: Root | null = null;

/**
 * Mount function for rendering from Rails
 */
export function mountIconPicker(
  container: HTMLElement,
  props: IconPickerContainerProps
): Root {
  // Clean up previous root if it exists
  if (iconPickerRoot) {
    iconPickerRoot.unmount();
  }

  const root = createRoot(container);
  iconPickerRoot = root;
  root.render(<IconPickerContainer {...props} />);
  return root;
}

/**
 * Unmount function for cleanup
 */
export function unmountIconPicker(): void {
  if (iconPickerRoot) {
    iconPickerRoot.unmount();
    iconPickerRoot = null;
  }
}

export default IconPickerContainer;
